/**
 * Módulo PAEBM (GUPM-201) — "PAEBM de bolso", fiel ao `renderMPAEBM` do V8 com
 * Design System. Tela informativa (sem fluxo complexo):
 *  - download por estrutura (A/B/C) — stub (docs ainda não anexados);
 *  - sequência de emergência + quando/o que fazer;
 *  - botão de emergência → MESMO contrato do header (`navigate('/tarp', emergencyNavState())`);
 *  - sirenes (informativo, via datasource) + contatos (via `contacts`/DB, sem PII hardcoded).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { emergencyNavState } from '@/domain';
import { PageHeader } from '@/ui';
import { useAuth } from '@/hooks/useAuth';
import { useDataSources } from '@/services/datasources';
import { useContatosEmergencia } from '@/hooks/useContatosEmergencia';
import { EtiquetasFuncao } from '@/ui/EtiquetasFuncao';
import {
  DOCS_PAEBM, O_QUE_FAZER, QUANDO_ACIONAR, mensagemDownloadStub,
} from './content';
import SequenciaEmergencia from './components/SequenciaEmergencia';

const CARD = 'rounded-xl border border-jf-border bg-jf-surface p-4';

export default function Paebm() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isOperador = profile === 'operador';
  const [downloadNote, setDownloadNote] = useState<string | null>(null);

  const { sirenes } = useDataSources();
  const { data: listaSirenes = [], error: sirenesError } = useQuery({ queryKey: ['sirenes'], queryFn: () => sirenes.list() });
  const { data: contatos = [], isLoading: loadingContatos, error: contatosError } = useContatosEmergencia();

  return (
    <div className="space-y-3">
      <PageHeader title="🆘 PAEBM — Plano de Ação de Emergência" />

      {/* Documentos por estrutura — botões stub (docs anexados depois pelo dono). */}
      <div className={CARD}>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-jf-muted">
          Documentos do PAEBM — Barragens A, B e C
        </div>
        <div className="space-y-2">
          {DOCS_PAEBM.map((d) => (
            <div
              key={d.dam}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-2.5 ${
                d.disponivel ? 'border-jf-yellow/30 bg-jf-yellow/[0.04]' : 'border-jf-border bg-white/[0.02]'
              }`}
            >
              <div>
                <div className={`text-[13px] font-bold ${d.disponivel ? 'text-white' : 'text-jf-muted'}`}>
                  📄 PAEBM — Barragem {d.dam} · Seção I
                </div>
                <div className="text-[10px] text-jf-dim">{d.cod} · {d.rev}</div>
              </div>
              <button
                type="button"
                onClick={() => setDownloadNote(mensagemDownloadStub(d.dam))}
                className={`rounded-lg px-3.5 py-1.5 text-[11px] font-bold transition ${
                  d.disponivel ? 'bg-jf-yellow text-jf-bg' : 'border border-jf-border text-jf-muted hover:text-white'
                }`}
              >
                {d.disponivel ? '⬇ Baixar PDF' : 'Solicitar à Foscentro'}
              </button>
            </div>
          ))}
        </div>
        {downloadNote && (
          <p role="status" className="mt-2 rounded-lg border border-dashed border-jf-border px-3 py-2 text-[11px] text-jf-dim">
            {downloadNote}
          </p>
        )}
        <p className="mt-2 text-[10px] text-jf-dim">
          As orientações gerais (sequência de emergência, ZAS, sirenes) são comuns às 3 estruturas e estão abaixo.
        </p>
      </div>

      {/* PAEBM de bolso — sequência de emergência */}
      <div className="flex items-center gap-2 pt-1">
        <span className="text-base">🧭</span>
        <span className="text-sm font-black tracking-wide text-jf-yellow">PAEBM DE BOLSO</span>
        <span className="text-[10px] text-jf-dim">— resumo rápido para consulta imediata</span>
      </div>
      <div className={CARD}>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-jf-dim">Sequência de emergência</div>
        <SequenciaEmergencia />
      </div>

      {/* Quando acionar */}
      <div className={`${CARD} border-l-[3px]`} style={{ borderLeftColor: '#ef4444' }}>
        <div className="mb-2 text-[12px] font-black uppercase tracking-wide text-level-emergencia">
          🚨 Quando acionar a emergência
        </div>
        <ul className="list-disc space-y-1 pl-5 text-[12.5px] leading-relaxed text-jf-muted">
          {QUANDO_ACIONAR.map((t) => <li key={t}>{t}</li>)}
        </ul>
      </div>

      {/* O que fazer agora + botão de emergência (mesmo contrato do header) */}
      <div className={`${CARD} border-l-[3px]`} style={{ borderLeftColor: '#FECB00' }}>
        <div className="mb-2 text-[12px] font-black uppercase tracking-wide text-jf-yellow">✅ O que fazer AGORA</div>
        <ol className="list-decimal space-y-1 pl-5 text-[12.5px] leading-relaxed text-jf-muted">
          {O_QUE_FAZER.map((t) => <li key={t}>{t}</li>)}
        </ol>
        <button
          type="button"
          onClick={() => navigate('/tarp', { state: emergencyNavState() })}
          className="mt-3 w-full rounded-lg bg-level-emergencia py-3 text-[13px] font-black uppercase tracking-wide text-white"
        >
          🆘 Abrir protocolo de emergência (TARP-EMG)
        </button>
      </div>

      {/* Sirenes ZAS — informativo (operação física é externa/TECAL) */}
      <div className={CARD}>
        <div className="mb-2 text-[12px] font-black text-white">🔊 Sirenes da Zona de Autossalvamento (ZAS)</div>
        {listaSirenes.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {listaSirenes.map((s) => (
              <span
                key={s.id}
                className={`rounded-full border px-2.5 py-1 font-mono text-[11px] font-bold ${
                  s.isOnline ? 'border-level-normal/30 bg-level-normal/10 text-level-normal' : 'border-jf-border text-jf-muted'
                }`}
              >
                {s.codigo}{s.isOnline ? '' : ' ○'}
              </span>
            ))}
          </div>
        )}
        {sirenesError && (
          <p role="status" className="mb-2 text-[11px] text-level-atencao">
            Status das sirenes indisponível no momento.
          </p>
        )}
        <p className="text-[11px] leading-relaxed text-jf-dim">
          Acionamento manual (sala de monitoramento) ou automático (tiltímetros, via AAS). Manutenção e operação
          física: TECAL, em sistema externo.
        </p>
      </div>

      {/* Contatos de emergência — do banco (contacts/RLS), nunca hardcoded */}
      <div className={CARD}>
        <div className="mb-2 text-[12px] font-black text-white">📞 Contatos de emergência</div>
        {loadingContatos ? (
          <p role="status" className="text-[11px] text-jf-dim">Carregando contatos…</p>
        ) : contatosError ? (
          <p role="status" className="text-[11px] text-level-atencao">Não foi possível carregar os contatos.</p>
        ) : contatos.length === 0 ? (
          <p className="text-[11px] text-jf-dim">Nenhum contato cadastrado ainda.</p>
        ) : (
          <ul className="divide-y divide-jf-border">
            {contatos.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-1.5">
                <div className="min-w-0">
                  <div className="truncate text-[12px] font-semibold text-white">{c.name}</div>
                  <EtiquetasFuncao contato={c} className="mt-0.5" />
                </div>
                {c.phone ? (
                  <a href={`tel:${c.phone}`} className="whitespace-nowrap text-[13px] font-bold text-jf-yellow">
                    {c.display}
                  </a>
                ) : (
                  <span className="whitespace-nowrap text-[13px] text-jf-dim">{c.display}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => navigate('/outros')}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-jf-border py-2 text-[11px] font-bold text-jf-muted transition hover:border-jf-yellow/40 hover:text-white"
        >
          📚 Ver todos os contatos (Outros)
        </button>
      </div>

      {/* Nota técnica — só perfil B (técnico responsável / admin) */}
      {!isOperador && (
        <div className="rounded-lg border border-[#78B4CF]/25 bg-[#78B4CF]/[0.07] p-3 text-[11px] leading-relaxed text-jf-muted">
          <strong className="text-[#93c5e8]">Nota técnica:</strong> o resumo acima é orientativo e não substitui o
          documento oficial do PAEBM (Rev.2/Abr 2026). A declaração de emergência no SIGBM, a notificação a agentes
          externos e a coordenação da evacuação seguem a matriz RACI (módulo 📚 Outros) e as atribuições do
          Coordenador do PAEBM.
        </div>
      )}
    </div>
  );
}
